import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_LIGHT, LOGO_DARK } from "@/lib/brand";

const LINKS = [
  { label: "Home", path: "/", testid: "nav-link-home" },
  { label: "Properties", path: "/listings", testid: "nav-link-properties" },
  { label: "About Us", path: "/about", testid: "nav-link-about" },
  { label: "News", path: "/news", testid: "nav-link-news" },
  { label: "Contact", path: "/contact", testid: "nav-link-contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const light = location.pathname === "/" && !scrolled && !open;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-ivory/90 backdrop-blur-md border-b hairline shadow-[0_8px_30px_-18px_rgba(20,20,20,0.3)]" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-8 py-5">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img
            src={light ? LOGO_DARK : LOGO_LIGHT}
            alt="SBS Properties"
            className="h-14 sm:h-16 w-auto transition-all duration-300"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-base font-medium whitespace-nowrap transition-colors duration-300 ${
                  isActive && !l.path.includes("#")
                    ? light ? "text-brand-gold" : "text-brand"
                    : light ? "text-ivory/80 hover:text-ivory" : "text-foreground/80 hover:text-brand"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <button
          data-testid="nav-mobile-menu-btn"
          className={`lg:hidden p-2 transition-colors duration-300 ${light ? "text-ivory" : "text-foreground"}`}
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-ivory border-t hairline">
          <div className="flex flex-col px-6 py-4 gap-1">
            {LINKS.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                data-testid={`mobile-${l.testid}`}
                className="py-3 text-base font-medium text-foreground border-b hairline last:border-0"
              >
                {l.label}
              </Link>
            ))}
            <Link to="/admin/login" data-testid="mobile-nav-staff-portal-btn" className="py-3 text-base font-medium text-brand">
              Staff Portal
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
